/**
 * adx — Average Directional Index (Wilder, 1978).
 *
 * Trend-strength oscillator that maps OHLC bars to a 0–100 series. Used
 * by `trendContextScore` to answer "is the market actually trending at
 * entry, or chopping?" — Raschke's Holy Grail explicitly gates entries
 * on ADX > 30, and the score uses ADX > 25 as the "trending regime"
 * +1 point.
 *
 * Algorithm (standard Wilder's):
 *   - TR  = max(high − low, |high − prevClose|, |low − prevClose|)
 *   - +DM = (high − prevHigh) if positive AND > (prevLow − low), else 0
 *   - −DM = (prevLow − low)   if positive AND > (high − prevHigh), else 0
 *   - Wilder-smooth each series with the chosen period (default 14):
 *       seed = sum of first `period` values
 *       next = prev − (prev / period) + current
 *   - +DI = 100 × smoothed(+DM) / smoothed(TR)
 *   - −DI = 100 × smoothed(−DM) / smoothed(TR)
 *   - DX  = 100 × |+DI − −DI| / (+DI + −DI)
 *   - ADX = Wilder-smoothed DX over the same period
 *
 * Warm-up: the first `2 × period` bars produce null ADX values (the
 * DI series itself takes `period` bars to seed, then ADX needs another
 * `period` of DX values to seed its own smoothing).
 *
 * Pure. No side effects. Returns a parallel array to bars.
 */

/**
 * Compute the ADX series for an OHLC bar array.
 *
 * @param {Array<{high:number, low:number, close:number, time?:number}>} bars  Sorted ascending.
 * @param {number} [period=14]  Standard ADX uses 14.
 * @returns {Array<{ time:number|undefined, plusDI:number|null, minusDI:number|null, adx:number|null }>}
 *          Same length as `bars`. Null values during warm-up.
 */
export const computeADX = (bars, period = 14) => {
  if (!Array.isArray(bars) || bars.length === 0) return [];
  const p = Math.floor(Number(period));
  if (!Number.isFinite(p) || p < 1) return [];

  const n = bars.length;
  const out = new Array(n);
  for (let i = 0; i < n; i++) {
    out[i] = { time: bars[i]?.time, plusDI: null, minusDI: null, adx: null };
  }

  if (n < 2) return out;

  // ── Step 1: per-bar TR, +DM, −DM ──────────────────────────────
  const tr = new Array(n);
  const plusDM = new Array(n);
  const minusDM = new Array(n);
  tr[0] = 0; plusDM[0] = 0; minusDM[0] = 0;  // no prev bar to compare

  for (let i = 1; i < n; i++) {
    const cur = bars[i];
    const prev = bars[i - 1];
    const hi = Number(cur?.high);
    const lo = Number(cur?.low);
    const cl = Number(cur?.close);
    const prevHi = Number(prev?.high);
    const prevLo = Number(prev?.low);
    const prevCl = Number(prev?.close);
    if (![hi, lo, cl, prevHi, prevLo, prevCl].every(Number.isFinite)) {
      // Bad bar — leave TR/DM at 0 so we don't propagate NaN into the
      // smoother (it would freeze the whole series). The bar's null ADX
      // entry stays null via the warm-up logic below.
      tr[i] = 0;
      plusDM[i] = 0;
      minusDM[i] = 0;
      continue;
    }
    tr[i] = Math.max(hi - lo, Math.abs(hi - prevCl), Math.abs(lo - prevCl));
    const upMove = hi - prevHi;
    const downMove = prevLo - lo;
    plusDM[i]  = (upMove > 0 && upMove > downMove) ? upMove : 0;
    minusDM[i] = (downMove > 0 && downMove > upMove) ? downMove : 0;
  }

  // ── Step 2: Wilder-smooth TR, +DM, −DM ────────────────────────
  // Need at least `period + 1` bars (1 prev + `period` for the seed sum).
  if (n <= p) return out;

  // Seed = sum of first `period` post-prev values, i.e. indexes 1..p (inclusive).
  let smTR = 0;
  let smPlusDM = 0;
  let smMinusDM = 0;
  for (let i = 1; i <= p; i++) {
    smTR += tr[i];
    smPlusDM += plusDM[i];
    smMinusDM += minusDM[i];
  }

  // Compute DI at the seed index (i = p). DX follows.
  // DX=0 when sum=0 (truly flat bars with no directional movement). That
  // value feeds the ADX smoother and lets ADX correctly DECAY through
  // chop instead of getting frozen at its prior value — important for the
  // "trend then flat" scenario in trendContextScore.
  const dxSeries = new Array(n).fill(null);
  if (smTR > 0) {
    const plusDI  = 100 * (smPlusDM  / smTR);
    const minusDI = 100 * (smMinusDM / smTR);
    out[p].plusDI = plusDI;
    out[p].minusDI = minusDI;
    const sum = plusDI + minusDI;
    dxSeries[p] = sum > 0 ? 100 * Math.abs(plusDI - minusDI) / sum : 0;
  }

  // Continue with Wilder-smoother for subsequent bars.
  // smoothed_next = smoothed_prev − (smoothed_prev / period) + current
  for (let i = p + 1; i < n; i++) {
    smTR      = smTR      - (smTR      / p) + tr[i];
    smPlusDM  = smPlusDM  - (smPlusDM  / p) + plusDM[i];
    smMinusDM = smMinusDM - (smMinusDM / p) + minusDM[i];
    if (smTR > 0) {
      const plusDI  = 100 * (smPlusDM  / smTR);
      const minusDI = 100 * (smMinusDM / smTR);
      out[i].plusDI = plusDI;
      out[i].minusDI = minusDI;
      const sum = plusDI + minusDI;
      dxSeries[i] = sum > 0 ? 100 * Math.abs(plusDI - minusDI) / sum : 0;
    } else {
      // TR=0 is exceptional (would mean a zero-range bar AND no prior-close
      // gap). Hold previous DX rather than introduce NaN; the next bar's
      // smoother will recover when TR returns positive.
      dxSeries[i] = dxSeries[i - 1] ?? 0;
    }
  }

  // ── Step 3: Wilder-smooth DX to produce ADX ──────────────────
  // The seed for ADX is the simple average of the first `period` DX
  // values, computed starting from index `p` (so seed spans p..p+p-1).
  // ADX warm-up: first `2*p - 1` bars produce null.
  const adxStart = 2 * p;  // index where ADX first has a value
  if (n <= adxStart) return out;

  // Need to ensure all DX values in p..adxStart-1 are finite.
  let dxSum = 0;
  let dxCount = 0;
  for (let i = p; i < adxStart; i++) {
    if (Number.isFinite(dxSeries[i])) { dxSum += dxSeries[i]; dxCount++; }
  }
  if (dxCount === 0) return out;
  let adx = dxSum / dxCount;
  out[adxStart - 1].adx = adx;  // seed lands at the last bar of the seed window

  for (let i = adxStart; i < n; i++) {
    if (!Number.isFinite(dxSeries[i])) {
      // Hold the prior ADX rather than poison with null — Wilder's
      // smoother itself does this naturally when one input is NaN.
      out[i].adx = adx;
      continue;
    }
    adx = ((adx * (p - 1)) + dxSeries[i]) / p;
    out[i].adx = adx;
  }

  return out;
};

/**
 * Read ADX value at the bar containing `ts`. Returns null when ADX is
 * not yet warmed up at that bar or the timestamp falls outside bars.
 *
 * @param {Array} adxSeries  Output of computeADX.
 * @param {number} ts        Unix seconds.
 * @returns {number|null}
 */
export const adxAtTime = (adxSeries, ts) => {
  if (!Array.isArray(adxSeries) || adxSeries.length === 0) return null;
  if (!Number.isFinite(ts)) return null;
  // Find the LAST bar whose time is ≤ ts (the bar containing ts at OPEN-time
  // semantics). Linear scan is fine — series are small.
  let chosen = null;
  for (const pt of adxSeries) {
    if (pt?.time == null) continue;
    if (pt.time <= ts) chosen = pt;
    else break;
  }
  return chosen?.adx ?? null;
};
